/**
 * Auto-default-workspace bootstrap.
 *
 * Runs once per app-shell mount (after the user is authenticated and
 * has at least one organization). If localStorage has no
 * `wiser_default_workspace`, calls `POST /api/workspaces/ensure-default/`
 * — Django returns an existing workspace or auto-creates "Default"
 * — then persists the result via `setDefaultWorkspace`.
 *
 * The bootstrap is a no-op when:
 *   - A default workspace is already stored.
 *   - The user has no active organization yet.
 *
 * Pages that rely on the default workspace (legacy short URLs like
 * `/organic-campaigns`, the saga unit page, etc.) read the same
 * localStorage key via `useDefaultWorkspace`, so once this hook
 * resolves them everything else hydrates automatically.
 */

import { useEffect, useRef } from "react";

import { apiFetch } from "@/lib/jwt-auth-client";
import { useDefaultWorkspace, type StoredWorkspace } from "@/hooks/useDefaultWorkspace";

interface EnsureResponseBody {
  id: string;
  slug: string;
  name: string;
  created: boolean;
}

export function useEnsureDefaultWorkspace(input: {
  activeOrganizationId: string | null | undefined;
  /** If false, skip the bootstrap (e.g. while the auth context is
   * still loading). The hook is intentionally tolerant of this flag
   * flipping to true later. */
  enabled: boolean;
}) {
  const { defaultWorkspaceId, setDefaultWorkspace } = useDefaultWorkspace();
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!input.enabled) return;
    if (!input.activeOrganizationId) return;
    if (defaultWorkspaceId) return; // already have one — never re-fetch.
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/api/workspaces/ensure-default/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            organization_id: input.activeOrganizationId,
          }),
        });
        if (!res.ok) return;
        const body = (await res.json()) as Partial<EnsureResponseBody>;
        if (cancelled || typeof body.id !== "string" || typeof body.slug !== "string") {
          return;
        }
        const ws: StoredWorkspace = { id: body.id, slug: body.slug };
        setDefaultWorkspace(ws);
      } catch {
        // Network/auth failures are non-fatal — the user will see the
        // standard "pick a workspace" UI in legacy URL fallbacks. We
        // don't surface a toast; the bootstrap retries on the next
        // app-shell mount.
      } finally {
        inFlightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [input.enabled, input.activeOrganizationId, defaultWorkspaceId, setDefaultWorkspace]);
}
